import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api } from '../lib/api';
import type { User } from '../types';

export interface RegisterInput {
  email: string;
  password: string;
  displayName?: string;
}

interface AuthContextValue {
  user: User | null;
  /** `loading` until the initial session check completes. */
  status: 'loading' | 'ready';
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (input: RegisterInput) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * A NON-SECRET flag cookie the Worker sets whenever it issues a session.
 *
 * WHY IT IS A COOKIE AND NOT localStorage
 *
 * The session lives in HttpOnly cookies, so this app cannot see whether it has one, and
 * it has to ask the server. Asking when there is nothing to restore costs two wasted
 * round-trips and makes the browser log two red 401s on every anonymous page load.
 *
 * The signal therefore has to come from whoever created the session. localStorage only
 * worked for the in-app sign-in form, because that is the only code that knew a session
 * had just been created. A Google sign-in is created by a server redirect, so a browser
 * landing on `/dashboard` straight from the OAuth callback had no local hint at all — the
 * app assumed "signed out" and bounced a signed-in user back to the sign-in page.
 *
 * A cookie set by the Worker is present for EVERY way a session can be created: password
 * sign-in, registration, token refresh and the Google callback. It carries no authority —
 * it is always `1`, the server never reads it, and forging it only causes one probe that
 * returns 401.
 */
const SESSION_HINT_COOKIE = 'ita6_has_session';

function readSessionHint(): boolean {
  try {
    return document.cookie
      .split(';')
      .some((part) => part.trim().startsWith(`${SESSION_HINT_COOKIE}=`));
  } catch {
    // Cookie access can be unavailable. Probing is the safe fallback.
    return true;
  }
}

/**
 * Drop the flag when a probe proves there is no session, so the next load does not ask
 * again. `Secure` must match how the Worker set it, or the delete would be ignored.
 */
function clearSessionHint(): void {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${SESSION_HINT_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  } catch {
    /* Storage unavailable — nothing to do, and nothing breaks. */
  }
}


/**
 * Authentication state is always derived from the Worker session (HttpOnly
 * cookies) — never from localStorage — so it cannot be spoofed client-side.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready'>('loading');

  const refresh = useCallback(async (): Promise<User | null> => {
    try {
      const me = await api.get<User>('/auth/me');
      setUser(me);
      return me;
    } catch (error) {
      // The access token may simply have expired: try one silent refresh.
      if (error instanceof ApiError && error.status === 401) {
        try {
          await api.post<User>('/auth/refresh');
          const me = await api.get<User>('/auth/me');
          setUser(me);
          return me;
        } catch {
          setUser(null);
          clearSessionHint();
          return null;
        }
      }
      setUser(null);
      clearSessionHint();
      return null;
    }
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      // Nothing to restore on a browser that has never held a session, so do not ask.
      if (readSessionHint()) {
        await refresh();
      } else {
        setUser(null);
      }
      if (active) setStatus('ready');
    })();
    return () => {
      active = false;
    };
  }, [refresh]);

  const login = useCallback(async (email: string, password: string): Promise<User> => {
    const me = await api.post<User>('/auth/login', { email, password });
    setUser(me);
    return me;
  }, []);

  const register = useCallback(async (input: RegisterInput): Promise<User> => {
    const me = await api.post<User>('/auth/register', input);
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } finally {
      setUser(null);
      clearSessionHint();
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      isAuthenticated: user !== null,
      isAdmin: user?.role === 'admin',
      login,
      register,
      logout,
      refresh,
    }),
    [user, status, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }
  return context;
}
